import { createFeatureSelector, createSelector } from '@ngrx/store';
import { D365AgentProfileType } from '../../common/enums/d365/d365-agent-profile-type';
import { D365AgentType } from '../../common/enums/d365/d365-agent-type';
import { D365ChannelCode } from '../../common/enums/d365/d365-channel-code';
import { AgentInfo, VirtualAgent } from '../../common/models/user/agent';
import { Contact } from '../../common/models/user/contact';
import { CustomerInfo } from '../../common/models/user/customer';
import { GeolocationMonitoring } from '../../common/models/user/geolocation-monitoring';
import { Lead } from '../../common/models/user/lead';
import { ContactLeadInfo } from '../models/select-contact-lead';
import { DataOwnershipChangeData, MastershipType, UserState } from '../models/user-state';
import { getContactNameFromUserState } from './selector-utility.functions';

export const userStateSelector = createFeatureSelector<UserState>('user');
export const selectUserState = createSelector(userStateSelector, (state: UserState) => state);
export const selectContactName = createSelector(userStateSelector, getContactNameFromUserState);
export const selectContact = createSelector(userStateSelector, (state: UserState) => state.contact);
export const selectLead = createSelector(userStateSelector, (state: UserState) => state.lead);
export const selectAgentInfo = createSelector(userStateSelector, (state: UserState) => state.agentInfo);
export const selectFromCrm = createSelector(userStateSelector, (state: UserState) => state.fromCrm);
export const selectCustomer = createSelector(userStateSelector, (state: UserState) => state.customerInfo);
export const selectCurrentVirtualAgent = createSelector(selectAgentInfo, (agInfo: AgentInfo) =>
    agInfo?.VirtualAgents?.find((x) => x.IsCurrentSelected === true),
);

export const selectChannelCode = createSelector(selectCurrentVirtualAgent, (va) => {
    if (va?.VirtualAgency) {
        return va.VirtualAgency.Channel.Code;
    }
    return '' as D365ChannelCode;
});

export const selectCurrentProfile = createSelector(
    selectCurrentVirtualAgent,
    selectAgentInfo,
    selectChannelCode,
    (selectedVa: VirtualAgent, currentAgInfo: AgentInfo, channelCode: D365ChannelCode) => {
        if (selectedVa && currentAgInfo) {
            return {
                selectedVirtualAg: selectedVa,
                currentAgentInfo: currentAgInfo,
                agentName: currentAgInfo.Agent.Name,
                agentCode: currentAgInfo.Agent.Code,
                agentType: currentAgInfo.Agent.AgentType as D365AgentType,
                channelCode,
                userProfile: currentAgInfo.Type as D365AgentProfileType,
            };
        }
        return null;
    },
);
export const selectIsSiebelCustomer = createSelector(
    userStateSelector,
    (state: UserState) => state.customerMastership === MastershipType.Siebel,
);

export const selectContactLead = createSelector(
    selectContact,
    selectLead,
    selectFromCrm,
    selectCustomer,
    selectIsSiebelCustomer,
    (
        contactState: Contact,
        leadState: Lead,
        fromCrmState: boolean,
        customerInfoState: CustomerInfo,
        isSiebelCustomer: boolean,
    ): ContactLeadInfo => ({
        contact: contactState,
        lead: leadState,
        fromCrm: fromCrmState,
        customerInfo: customerInfoState,
        isSiebelCustomer,
    }),
);

export const selectEmailAndTel = createSelector(selectUserState, (state): EmailAndTelSelection => {
    if (state && state.contact) {
        return {
            email: state.contact.emailaddress1,
            tel: state.contact.mobilephone || state.contact.telephone1,
            prefix: state.contact.prefixMobilephone || state.contact.prefixTelephone1,
            phone2: state.contact.telephone2,
            preferredContact: state.contact.preferredContact,
        };
    }
    return null;
});

export const selectLastUsedSegment = createSelector(selectAgentInfo, (agentInfo: AgentInfo) => {
    if (agentInfo && agentInfo.UserConfiguration) {
        return agentInfo.UserConfiguration.LastUsedCustomerSegment;
    }
    return null;
});

export const selectCartSegment = createSelector(userStateSelector, (userState: UserState) => userState.cartSegment);
export const selectUserProfile = createSelector(selectAgentInfo, (agentInfo: AgentInfo) => agentInfo?.ProfileCode);
export const selectCurrentAgentMail = createSelector(
    selectCurrentVirtualAgent,
    (currentVirtualAg: VirtualAgent) => currentVirtualAg.DefaultAgencyBranch.Email,
);

export const selectBillingAccountId = createSelector(
    userStateSelector,
    (state: UserState) => state.contact?.egl_customercode, // Non modificare, la getPaymentTool vuole questo campo
);
export const selectChannelInfo = createSelector(selectCurrentVirtualAgent, (selectedVa) => {
    return selectedVa?.VirtualAgency?.Channel;
    /*if (selectedVa && selectedVa?.VirtualAgency && selectedVa?.VirtualAgency?.Channel){
       
    }
    return null;*/
});

export const selectDataOwnershipChangeState = createSelector(
    userStateSelector,
    (state: UserState) => state?.dataOwnershipChange?.currentState || '',
);

export const selectDataOwnershipChangeData = createSelector(
    userStateSelector,
    (state: UserState) => state?.dataOwnershipChange?.data || new DataOwnershipChangeData(),
);

export const selectGeolocationMonitoring = createSelector(
    userStateSelector,
    (state: UserState) => state?.geolocationMonitoring || new GeolocationMonitoring(),
);

export interface EmailAndTelSelection {
    email: string;
    tel: string;
    prefix: string;
    phone2: string;
    preferredContact: string;
}
